import { DestroyRef, inject, Injectable } from '@angular/core';
import { catchError, defer, exhaustMap, firstValueFrom, forkJoin, Observable, of, take } from 'rxjs';
import { CommonDataService } from '../common-data.service';
import { CollaborationApiService } from '../api/collaboration-api.service';
import { CollaborationHistoryData, CollaborationState, CollaboratorDetails, DiscardCollabRequest, EnableCollaborationRequest, DisableCollaborationRequest, EstimateSaveRequest, ModifyCollaborationRequest, OpenEstimateRequest, OpenEstimateResponse, PublishRequest, SetUserPreferencesRequest, TurnoverToSalesRequest, TurnoverToSalesResponse, VerifyCollaboratorsRequest, VerifyCollaboratorResultItem, CollaborationVerificationResult, CollaboratorPreferences, CollaborationHistoryEnum, AccessRequest, CollaborationHubData } from '../../models/dtos';
import { siQuoteUrls } from '../../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { PublishBeforeCloseDialogComponent } from '../../Components/dialogs/common/publish-before-close-dialog/publish-before-close-dialog.component';
import { CONSTANTS } from '../../Constants/Constants';
import { UserProfileService } from './user-profile.service';
import { TranslateService } from '@ngx-translate/core';
import { IModifiedCollaborators } from '../../models/collaboration/IModifiedcollaborators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IVerifyCollaboratorResultData, IVerifyMultipleCollaborators } from '../../models/collaboration/IVerifyMultipleCollaborators';
import { PreferenceApiService } from '../api/preference.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiPollingService } from './api-polling.service';
import { OpenEstimateApiService } from '../api/open-estimate.service';
import { OpenEstimateOperationsService } from './open-estimate-operations.service';
import { NotificationMessageService } from './notification-message.service';
import { ICollaborationPollingSnapshot } from '../../models/collaboration/ICollaborationPollingSnapshot';
import { CommonFunctionsService } from '../common-functions.service';

@Injectable({
  providedIn: 'root'
})
export class CollaborationOperationsService {

  private destroyRef = inject(DestroyRef);
  private pollingStarted = false;
  private isReopeningEstimate = false;
  private reconcileOnNextPublication = false;
  private notifiedRemovedCollaboratorEstimateId = '';

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly preferenceApiService: PreferenceApiService,
    private readonly collaborationApiService: CollaborationApiService,
    private readonly userProfileService: UserProfileService,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly apiPollingService: ApiPollingService,
    private readonly notificationMessageService: NotificationMessageService,
    private readonly openEstimateApiService: OpenEstimateApiService,
    private readonly openEstimateOperationsService: OpenEstimateOperationsService,
    private readonly commonFunctionsService: CommonFunctionsService
  ) {
    this.subscribeCollaborationData();
  }

  private subscribeCollaborationData(): void {
    this.commonDataService.collaborationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (this.reconcileOnNextPublication) {
          this.reconcileOnNextPublication = false;
          this.reconcileReopenedCollaboration();
        }
        if (this.commonDataService.getCollaborationState() !== CollaborationState.Edit) {
          this.commonDataService.setAccessRequests([]);
        }
        if (!data) {
          this.notifiedRemovedCollaboratorEstimateId = '';
        }
      });
  }

  startCollaborationPolling(): void {
    if (this.pollingStarted) {
      return;
    }
    this.pollingStarted = true;
    this.subscribeAccessRequestPolling();
    this.subscribeCollaborationDefinitionPolling();
  }

  private subscribeAccessRequestPolling(): void {
    this.apiPollingService.pollAccessRequests()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          if (result.estimateId && result.estimateId !== this.commonDataService.getEstimateId()) {
            return;
          }
          this.commonDataService.setAccessRequests(result.accessRequests ?? []);
          this.handleAccessRequestResponse(result.declineAccessResponse);
        },
        error: () => console.error('Error while polling for access requests')
      });
  }

  private subscribeCollaborationDefinitionPolling(): void {
    this.apiPollingService.pollCollaborationDefinition()
      .pipe(
        exhaustMap(snapshot => defer(() => this.applyCollaborationPollingSnapshot(snapshot)).pipe(
          catchError(error => {
            console.error('Unable to apply collaboration polling snapshot:', error);
            return of(undefined);
          })
        )),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: () => console.error('Error while polling collaboration definition')
      });
  }

  private handleAccessRequestResponse(response: { declineMessage: string; isPending: boolean } | null): void {
    if (!response) {
      return;
    }

    this.commonDataService.setAccessRequestPending(response.isPending);
    this.commonDataService.setAccessRequestDeclineMessage(response.isPending ? '' : response.declineMessage ?? '');
  }

  private async applyCollaborationPollingSnapshot(
    snapshot: ICollaborationPollingSnapshot | null
  ): Promise<void> {
    if (!snapshot || snapshot.estimateId !== this.commonDataService.getEstimateId() ||
      snapshot.estimateSession !== this.commonDataService.getEstimateSession() ||
      this.commonDataService.getCollaborationState() === CollaborationState.Edit) {
      return;
    }

    const { collaborationDefinition, collaborationHistory } = snapshot.collaborationInfo;
    if (!collaborationDefinition) {
      return;
    }

    const collaborators = collaborationDefinition.collaborators ?? [];
    const userEmail = this.userProfileService.userInfo?.mail ?? CONSTANTS.EMPTY_STRING;
    const isCurrentUserCollaborator = collaborators.some(
      collaborator => collaborator.email?.toLowerCase() === userEmail.toLowerCase()
    );
    if (collaborationDefinition.collaborationState === CollaborationState.NonCollaborative ||
      !isCurrentUserCollaborator) {
      this.commonDataService.setCollaborationPollingSnapshot([], [], CollaborationState.NonCollaborative);
      this.commonDataService.enableReadOnlyMode(true);
      this.commonDataService.setAccessRequestPending(false);
      this.commonDataService.setAccessRequestDeclineMessage('');
      this.commonDataService.setAccessRequests([]);
      if (this.notifiedRemovedCollaboratorEstimateId !== snapshot.estimateId) {
        this.notifiedRemovedCollaboratorEstimateId = snapshot.estimateId;
        this.notificationMessageService.showWarning(this.translate.instant(
          collaborationDefinition.collaborationState === CollaborationState.NonCollaborative
            ? 'COLLABORATION_DISABLED' : 'COLLABORATION_REMOVED_MESSAGE'
        ));
      }
      return;
    }

    this.notifiedRemovedCollaboratorEstimateId = '';
    this.handleAccessRequestResponse(snapshot.collaborationInfo.accessRequestResponse);

    const currentEditor = collaborationHistory?.currentEditor;
    const editorEmail = currentEditor?.email?.toLowerCase() ?? '';
    const hasCurrentEditor = editorEmail.length > 0;
    const nextState = !hasCurrentEditor
      ? CollaborationState.Open
      : CollaborationState.Locked;

    const serverModifiedDate = snapshot.collaborationInfo.estimateStatus?.estimateModifiedDate;
    const localModifiedDate = this.commonDataService.getEstimateData()?.modifiedDate;
    const serverTimestamp = this.commonFunctionsService.collaborationTimestamp(serverModifiedDate);
    const localTimestamp = this.commonFunctionsService.collaborationTimestamp(localModifiedDate);
    const modifiedDateChanged = Number.isFinite(serverTimestamp) && Number.isFinite(localTimestamp)
      && serverTimestamp !== localTimestamp;
    const serverStatus = snapshot.collaborationInfo.estimateStatus;
    const costReviewStatusChanged = serverStatus?.costReviewStatus > 0
      && serverStatus.costReviewStatus !== this.commonDataService.getCostReviewData()?.status;
    const estimateStatusChanged = serverStatus?.estimateStatusId != null
      && serverStatus.estimateStatusId !== this.commonDataService.getEstimateData()?.estimateStatusId;
    if (estimateStatusChanged && !this.commonDataService.isArchivedEstimate()) {
      this.commonDataService.patchEstimate({ estimateStatusId: serverStatus.estimateStatusId });
    }

    const mappedHistory = (collaborationHistory?.collaborationHistory ?? []).map((item): CollaborationHistoryData => {
      return {
        collaboratorName: item.collaboratorName,
        modifiedDate: item.modifiedDate,
        historyId: item.operation,
        modificationNotes: item.modificationNotes,
        isCurrentEditor: false,
        collaboratorEmail: ''
      };
    });
    if (hasCurrentEditor) {
      mappedHistory.unshift({
        collaboratorName: currentEditor.name,
        modifiedDate: collaborationHistory.checkOutTime,
        historyId: null,
        modificationNotes: '',
        isCurrentEditor: true,
        collaboratorEmail: currentEditor.email
      });
    }

    this.commonDataService.setCollaborationPollingSnapshot(collaborators, mappedHistory, nextState);
    if (nextState !== CollaborationState.Locked) {
      this.commonDataService.setAccessRequestPending(false);
      this.commonDataService.setAccessRequestDeclineMessage('');
    }
    if (modifiedDateChanged || costReviewStatusChanged || estimateStatusChanged) {
      await this.reopenEstimate(snapshot.estimateId);
    }
  }


  async reopenEstimate(estimateId: string): Promise<boolean> {
    const canReopen = () => this.commonDataService.getCollaborationState() !== CollaborationState.Edit
      && !this.commonDataService.isArchivedEstimate();
    if (this.isReopeningEstimate || !navigator.onLine || !canReopen()) {
      return false;
    }

    this.isReopeningEstimate = true;
    try {
      const request: OpenEstimateRequest = {
        userId: this.userProfileService.userInfo?.gid ?? CONSTANTS.EMPTY_STRING,
        estimateId,
        userEmailId: this.userProfileService.userInfo?.mail ?? CONSTANTS.EMPTY_STRING,
        culture: this.commonDataService.getCurrentApplicationCulture(),
        isArchive: this.commonDataService.isArchivedEstimate()
      };
      const response = await firstValueFrom(this.openEstimateApiService.openEstimate(request).pipe(take(1)));
      if (!response || !canReopen()) {
        return false;
      }

      this.reconcileOnNextPublication = true;
      await this.openEstimateOperationsService.openEstimate(response);
      return true;
    } catch (error) {
      console.error('Unable to refresh the collaborative estimate from the server:', error);
      return false;
    } finally {
      this.reconcileOnNextPublication = false;
      this.isReopeningEstimate = false;
    }
  }

  private reconcileReopenedCollaboration(): void {
    if (this.commonDataService.getCollaborationState() === CollaborationState.Edit) {
      this.commonDataService.setCollaborationState(CollaborationState.Locked);
    }
    const userEmail = this.userProfileService.userInfo?.mail?.toLowerCase() ?? '';
    if (this.commonDataService.getCollaborationState() === CollaborationState.NonCollaborative ||
      !this.commonDataService.getCollaboratorData().some(collaborator => collaborator.email?.toLowerCase() === userEmail)) {
      this.commonDataService.enableReadOnlyMode(true);
      this.commonDataService.setAccessRequestPending(false);
      this.commonDataService.setAccessRequestDeclineMessage('');
    }
  }

  /**
   * Opens the Publish / Discard / Cancel dialog and returns its afterClosed()
   * observable. Result values: 'publish' | 'discard' | 'cancel'.
   * Callers handle post-dialog actions themselves.
   */
  openPublishBeforeCloseDialog(): Observable<string> {
    const dialogRef = this.dialog.open(PublishBeforeCloseDialogComponent, {
      width: CONSTANTS.COLLABORATION_POPUP_WIDTH,
      disableClose: true
    });
    return dialogRef.afterClosed();
  }

  /** Calls the PublishChanges API and awaits the result. */
  async performPublish(request: PublishRequest): Promise<boolean> {
    try {
      const result = await firstValueFrom(this.collaborationApiService.publishChanges(request));
      if (result) {
        this.commonDataService.setCollaborationState(CollaborationState.Open);
      }
      return result;
    } catch (error: HttpErrorResponse | unknown) {

      // Discard if estimate is already been published
      if ((error as HttpErrorResponse)?.error?.errorCode === 1519) {
        //TODO: Changes after common-function refactoring
        // const res = await this.performDiscard(false);
        // // this.openEstimateOperationsService.openEstimate(res);
      }
      return false;
    }
  }

  /** Calls the DiscardChanges API and awaits the result. Returns the response for callers that need to reopen the estimate. */
  async performDiscard(isEstimateClosing: boolean = false, suppressGlobalError: boolean = false): Promise<OpenEstimateResponse | null> {
    const request: DiscardCollabRequest = {
      isEstimateClosing,
      openEstimateRequest: {
        userId: this.userProfileService.userInfo?.gid ?? CONSTANTS.EMPTY_STRING,
        estimateId: this.commonDataService.getEstimateId(),
        userEmailId: this.userProfileService.userInfo?.mail ?? CONSTANTS.EMPTY_STRING,
        culture: this.commonDataService.getCurrentApplicationCulture(),
        isArchive: this.commonDataService.isArchivedEstimate(),
      }
    };
    return await firstValueFrom(this.collaborationApiService.discardChanges(request, suppressGlobalError))
      .catch((err: unknown): null => { console.error('DiscardChanges failed', err); return null; });
  }

  /**
   * Fires a DiscardChanges call via navigator.sendBeacon — safe to call during
   * window unload/pagehide since the request survives page teardown.
   * No-op if the estimate is not in a collaborative Edit state.
   */
  sendBeaconDiscard(): void {
    if (this.commonDataService.getCollaborationState() !== CollaborationState.Edit) return;
    const url = `${siQuoteUrls.collaboration}/DiscardChanges`;
    const body = new Blob(
      [JSON.stringify({ estimateId: this.commonDataService.getEstimateId(), additionalInfo: '' })],
      { type: 'application/json' }
    );
    navigator.sendBeacon(url, body);
  }

  getTranslatedCollaborationHistory(id: CollaborationHistoryEnum): string {
    let translatedText = CONSTANTS.EMPTY_STRING;
    switch (id) {
      case CollaborationHistoryEnum.CollaborationEnabled:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_COLLABORATION_ENABLED');
        break;
      case CollaborationHistoryEnum.CollaborationDisabled:
        translatedText = this.translate.instant('COLLABORATION_DISABLED');
        break;
      case CollaborationHistoryEnum.ModifiedCollaborators:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_MODIFIED_COLLABORATORS');
        break;
      case CollaborationHistoryEnum.Published:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_PUBLISHED');
        break;
      case CollaborationHistoryEnum.OpportunityLinked:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_OPPORTUNITY_LINKED');
        break;
      case CollaborationHistoryEnum.OpportunityUnlinked:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_OPPORTUNITY_UNLINKED');
        break;
      case CollaborationHistoryEnum.ProposalGenerated:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_PROPOSAL_GENERATED');
        break;
      case CollaborationHistoryEnum.CostReviewRequested:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_COST_REVIEW_REQUESTED');
        break;
      case CollaborationHistoryEnum.ProductPromotion:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_PRODUCT_PROMOTION');
        break;
      case CollaborationHistoryEnum.SAPSubmitted:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_SAP_SUBMITTED');
        break;
      case CollaborationHistoryEnum.PublishedFromMT:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_PUBLISHED_FROM_MT');
        break;
      case CollaborationHistoryEnum.SAPSubmittalUnlock:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_SAP_SUBMITTAL_UNLOCK');
        break;
      case CollaborationHistoryEnum.OpportunityCreated:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_OPPORTUNITY_CREATED');
        break;
      case CollaborationHistoryEnum.OpportunityCreateFailed:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_OPPORTUNITY_CREATE_FAILED');
        break;
      case CollaborationHistoryEnum.CostReviewUnlocked:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_COST_REVIEW_UNLOCKED');
        break;
      case CollaborationHistoryEnum.CostReviewAutoUnlocked:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_COST_REVIEW_AUTO_UNLOCKED');
        break;
      case CollaborationHistoryEnum.OpportunityLinkFailed:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_OPPORTUNITY_LINK_FAILED');
        break;
      case CollaborationHistoryEnum.OpportunityUnlinkFailed:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_OPPORTUNITY_UNLINK_FAILED');
        break;
      case CollaborationHistoryEnum.CostReviewCancelled:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_COST_REVIEW_CANCELLED');
        break;
      case CollaborationHistoryEnum.CostReviewerReplaced:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_COST_REVIEWER_REPLACED');
        break;
      case CollaborationHistoryEnum.CostReviewerRemoved:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_COST_REVIEWER_REMOVED');
        break;
      case CollaborationHistoryEnum.TurnoverToSales:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_TURNOVER_TO_SALES');
        break;
      case CollaborationHistoryEnum.PriceRecommenderUsed:
        translatedText = this.translate.instant('COLLABORATION_HISTORY_ENUM_PRICE_RECOMMENDER_USED');
        break;
      default:
        translatedText = CONSTANTS.EMPTY_STRING;
    }
    return translatedText;
  }

  canEnableAddCollaborator(arr: any[]): boolean {
    return arr.length - 1 < CONSTANTS.MAX_COLLABORATORS;
  }

  getModifiedCollaborators(initialState: CollaboratorDetails[], currentState: CollaboratorDetails[]): IModifiedCollaborators {
    const initialMap = new Map(initialState.map(c => [c.email, c]));
    const currentMap = new Map(currentState.map(c => [c.email, c]));

    const added: CollaboratorDetails[] = [];
    const altered: CollaboratorDetails[] = [];
    const removed: CollaboratorDetails[] = [];
    let primaryUserChanged = false;

    // Find added and altered collaborators by iterating current state
    for (const [email, current] of currentMap) {
      const initial = initialMap.get(email);
      if (!initial) {
        added.push(current);
        continue;
      } 
      if (initial.canEdit !== current.canEdit) {
        altered.push(current);
      }
      if (initial.isPrimaryUser !== current.isPrimaryUser) {
        primaryUserChanged = true;
      }
    }

    // Find removed collaborators by checking what was in initial state
    for (const [email, initial] of initialMap) {
      if (!currentMap.has(email)) {
        removed.push(initial);
      }
    }

    return { added, removed, altered, primaryUserChanged };
  }

  getCollaborationModificationNotes(modifiedCollaborators: IModifiedCollaborators): string {
    // Incorrect text here, need to add based on input parameters
    return this.translate.instant("COLLABORATION_HISTORY_ENUM_MODIFIED_COLLABORATORS");
  }

  async enableCollaboration(collaboratorsList: CollaboratorDetails[], estimateSaveRequest: EstimateSaveRequest): Promise<CollaborationHubData> {
    const request: EnableCollaborationRequest = {
      modificationNotes: this.getTranslatedCollaborationHistory(CollaborationHistoryEnum.CollaborationEnabled),
      collaborators: collaboratorsList,
      estimateSaveRequest: estimateSaveRequest
    };
    try {
      const response = await firstValueFrom(this.collaborationApiService.enableCollaboration(request));
      return response;
    } catch (err) {
      return null;
    }
  }

  async disableCollaboration(collaboratorsList: CollaboratorDetails[], estimateSaveRequest: EstimateSaveRequest): Promise<boolean> {
    const request: DisableCollaborationRequest = {
      collaborators: collaboratorsList,
      modificationNotes: this.getTranslatedCollaborationHistory(CollaborationHistoryEnum.CollaborationDisabled),
      estimateSaveRequest: estimateSaveRequest
    };
    try {
      const response = await firstValueFrom(this.collaborationApiService.disableCollaboration(request));
      return response;
    } catch (err) {
      return false;
    }
  }

  async modifyCollaboration(collaboratorsList: CollaboratorDetails[], modificationNotes: string, modifiedCollaborators: IModifiedCollaborators): Promise<CollaborationHubData> {    
    let collaborationAdditionalInfo: string = this.getAdditionalNotes(modifiedCollaborators)
    const request: ModifyCollaborationRequest = {
      modificationNotes: modificationNotes,
      collaborators: collaboratorsList,
      estimateName: this.commonDataService.getEstimateName() ?? CONSTANTS.EMPTY_STRING,
      cultureId: Number(this.commonDataService.getCurrentApplicationCulture()) || 1,
      additionalInfo: collaborationAdditionalInfo
    };
    try {
      const response = await firstValueFrom(this.collaborationApiService.modifyCollaboration(request));
      return response;
    } catch (err) {
      return null;
    }
  }

  private getAdditionalNotes (modifiedCollaborators: IModifiedCollaborators): string {
      let additionalNotes: string[] = []
      if (modifiedCollaborators.added?.length) {
        additionalNotes.push(CONSTANTS.COLLABORATORS_ADDED);
      }
      if (modifiedCollaborators.removed?.length) {
        additionalNotes.push(CONSTANTS.COLLABORATORS_REMOVED);
      }
      if (modifiedCollaborators.altered?.length) {
        additionalNotes.push(CONSTANTS.COLLABORATORS_ACCESS_LEVEL_CHANGED);
      }
      if (modifiedCollaborators.primaryUserChanged) {
        additionalNotes.push(CONSTANTS.PRIMARY_USER_CHANGED);
      }
      return additionalNotes.join(CONSTANTS.COMMA);
    }

  async requestAccess(changeLogNotes: string, sendEmail: boolean): Promise<boolean> {
    const request: AccessRequest = {
      requestMessage: changeLogNotes,
      requestorName: this.userProfileService.userInfo?.name ?? CONSTANTS.EMPTY_STRING,
      estimateName: this.commonDataService.getEstimateName() ?? CONSTANTS.EMPTY_STRING,
      sendEmail: sendEmail,
      cultureId: Number(this.commonDataService.getCurrentApplicationCulture()) || CONSTANTS.ONE
    }
    try {
      const response = await firstValueFrom(this.collaborationApiService.raiseAccessRequest(request));
      const succeeded = response !== false;
      if (succeeded) {
        this.commonDataService.setAccessRequestPending(true);
      }
      return succeeded;
    } catch (err) {
      return false;
    }
  }

  async verifyMultipleCollaborators(emails: string[], availableCollaborators: CollaboratorDetails[]): Promise<IVerifyMultipleCollaborators> {
    try {
      let res = await firstValueFrom(
        forkJoin({
          collaborators: emails.length ? this.collaborationApiService.verifyCollaborators({ collaboratorEmails: emails, isArchive: this.commonDataService.isArchivedEstimate() }) : of([]),
          availableCollaborators: availableCollaborators.length ? this.collaborationApiService.verifyCollaborators({ collaboratorEmails: availableCollaborators.map(c => c.email), isArchive: this.commonDataService.isArchivedEstimate() }) : of([])
        }));
      let results: IVerifyMultipleCollaborators = {
        collaborators: [],
        availableCollaborators: []
      };
      results.collaborators = res.collaborators.map(c => {
        const matchCollaborator = this.commonDataService.getAllCollaboratorPreferences()?.collaborators.find(collaborator => collaborator.email === c.collaboratorEmail);
        return {
          ...c,
          name: matchCollaborator ? matchCollaborator.name : c.collaboratorEmail
        };
      });
      results.availableCollaborators = res.availableCollaborators.map(c => {
        const matchCollaborator = this.commonDataService.getAllCollaboratorPreferences()?.availableCollaborators.find(collaborator => collaborator.email === c.collaboratorEmail);
        return {
          ...c,
          name: matchCollaborator ? matchCollaborator.name : c.collaboratorEmail
        };
      });
      const invalidCollaborators = await this.removeInvalidCollaborators(results);
      return invalidCollaborators;
    } catch (error) {
      return { collaborators: [], availableCollaborators: [] };
    }

  }

  private async removeInvalidCollaborators(results: IVerifyMultipleCollaborators): Promise<IVerifyMultipleCollaborators> {
    let invalidVerifiedCollaborators: IVerifyCollaboratorResultData[] = [];
    let validVerifiedCollaborators: IVerifyCollaboratorResultData[] = [];

    for (let i = 0; i < results.collaborators.length; i++) {
      if (results.collaborators[i].result !== CollaborationVerificationResult.Success && results.collaborators[i].result !== CollaborationVerificationResult.SelfAddNotAllowed) {
        invalidVerifiedCollaborators.push(results.collaborators[i]);
      } else {
        validVerifiedCollaborators.push(results.collaborators[i]);
      }
    }

    let invalidVerifiedAvailableCollaborators: IVerifyCollaboratorResultData[] = [];
    let validVerifiedAvailableCollaborators: IVerifyCollaboratorResultData[] = [];
    for (let i = 0; i < results.availableCollaborators.length; i++) {
      if (results.availableCollaborators[i].result !== CollaborationVerificationResult.Success && results.availableCollaborators[i].result !== CollaborationVerificationResult.SelfAddNotAllowed) {
        invalidVerifiedAvailableCollaborators.push(results.availableCollaborators[i]);
      } else {
        validVerifiedAvailableCollaborators.push(results.availableCollaborators[i]);
      }
    }

    if (invalidVerifiedCollaborators.length > 0 || invalidVerifiedAvailableCollaborators.length > 0) {
      let updatedPreferenceCollaboratos = this.commonDataService.getAllCollaboratorPreferences()?.collaborators.filter(c => !invalidVerifiedCollaborators.some(ivc => ivc.collaboratorEmail === c.email)) ?? [];
      // updatedPreferenceCollaboratos = updatedPreferenceCollaboratos.filter(c => !invalidVerifiedAvailableCollaborators.some(ivac => ivac.collaboratorEmail === c.email));
      let updatedPreferenceAvailableCollaboratos = this.commonDataService.getAllCollaboratorPreferences()?.availableCollaborators.filter(ac => !invalidVerifiedAvailableCollaborators.some(ivac => ivac.collaboratorEmail === ac.email)) ?? [];
      // updatedPreferenceAvailableCollaboratos = updatedPreferenceAvailableCollaboratos.filter(ac => !invalidVerifiedCollaborators.some(ivc => ivc.collaboratorEmail === ac.email));
      const collaboratorPreferences: CollaboratorPreferences = {
        collaborators: updatedPreferenceCollaboratos,
        availableCollaborators: updatedPreferenceAvailableCollaboratos
      };
      const result = await this.setCollaborationPreferences(collaboratorPreferences);

      if (result) {
        results.collaborators = validVerifiedCollaborators;
        results.availableCollaborators = validVerifiedAvailableCollaborators;
      }
    }
    return {
      collaborators: invalidVerifiedCollaborators,
      availableCollaborators: invalidVerifiedAvailableCollaborators
    };
  }

  async verifyAllCollaborators(emails: string[]): Promise<VerifyCollaboratorResultItem[]> {

    if (emails.length === 0) {
      return [];
    }
    const request: VerifyCollaboratorsRequest = {
      collaboratorEmails: emails,
      isArchive: this.commonDataService.isArchivedEstimate()
    };
    try {
      const result = await firstValueFrom(this.collaborationApiService.verifyCollaborators(request));
      return result;
    } catch (error) {
      return [];
    }
  }

  async setCollaborationPreferences(collaboratorPreferences: CollaboratorPreferences): Promise<boolean> {
    const requestBody: SetUserPreferencesRequest = {
      userId: this.userProfileService.userInfo?.gid ?? '',
      preferences: null,
      firePreferences: null,
      esPreferences: null,
      proposalPreferences: null,
      collaboratorPreferences: collaboratorPreferences,
      costReviewerPreferences: null,
      taxDetailsPreferences: null,
      resetPreferencesToDefault: false
    };
    try {
      const result = await firstValueFrom(this.preferenceApiService.setPreference(requestBody));
      if (result) {
        this.commonDataService.setCollaboratorDetails(collaboratorPreferences);
        return true;
      } else {
        return false;
      }
    } catch {
      return false;
    }
  }

  turnoverToSales(
    modificationNotes: string,
    saveRequest: EstimateSaveRequest,
    handleGocRestriction?: (error: unknown) => Promise<boolean>
  ): void {

    let turnOverToSalesRequest = new TurnoverToSalesRequest();
    turnOverToSalesRequest.saveRequest = saveRequest;
    turnOverToSalesRequest.collaborators = this.commonDataService.getCollaboratorData();
    turnOverToSalesRequest.turnoverUpdateNotes = modificationNotes;
    turnOverToSalesRequest.collaboratorsModificationNotes = this.translate.instant('COLLABORATION_HISTORY_ENUM_MODIFIED_COLLABORATORS');

    const owner = turnOverToSalesRequest.collaborators.find(collab => collab.roleId === CONSTANTS.ZERO);
    const primaryUser = turnOverToSalesRequest.collaborators.find(collab => collab.isPrimaryUser);

    turnOverToSalesRequest.isOwnershipChangeNeeded = owner?.email !== primaryUser?.email;

    this.collaborationApiService.turnoverToSales(turnOverToSalesRequest, !!handleGocRestriction)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: TurnoverToSalesResponse) => {
          if (response && response.ttS_Details) {
            const otherMetaData = this.commonDataService.getOtherMetaData();
            if (otherMetaData) {
              otherMetaData.ttsDetails = response.ttS_Details;
              this.commonDataService.patchOtherMetaData(otherMetaData);
            }
            if (turnOverToSalesRequest.isOwnershipChangeNeeded && response.modifiedCollaborators) {
              this.commonDataService.setCollaboratorDataList(response.modifiedCollaborators);
            }
            if (this.commonDataService.isCollaborationEstimate() && response.collabHubData) {
              const collaborationHubData = response.collabHubData;
              this.commonFunctionsService.updateCollaborationHubData(collaborationHubData);
            }
          }
        },
        error: async (error: unknown) => {
          if (handleGocRestriction && await handleGocRestriction(error)) {
            return;
          }

          console.error('TurnoverToSales failed', error);
        }
      });
  }
}
